// src/feedback/feedbackLogbook.ts — Parent Deck Feedback Archive & GitHub Exporter

import { tokens } from '../ui/designTokens';
import { soundEngine } from '../audio/soundEngine';
import type { FeedbackCategory, FeedbackEntry } from './feedbackTypes';
import { FeedbackStorage } from './feedbackStorage';

export class FeedbackLogbook {
  private container: HTMLElement;
  private activeCategoryFilter: 'all' | FeedbackCategory = 'all';

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'feedback-logbook-deck';
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  render(): HTMLElement {
    const allEntries = FeedbackStorage.loadEntries();
    const filtered =
      this.activeCategoryFilter === 'all'
        ? allEntries
        : allEntries.filter((e) => e.category === this.activeCategoryFilter);

    const categories: Array<{ id: 'all' | FeedbackCategory; label: string }> = [
      { id: 'all', label: `All (${allEntries.length})` },
      { id: 'bug', label: `🐛 Bugs (${allEntries.filter((e) => e.category === 'bug').length})` },
      { id: 'adhd_friction', label: `🧠 ADHD (${allEntries.filter((e) => e.category === 'adhd_friction').length})` },
      { id: 'curriculum', label: `📚 11+ Curriculum (${allEntries.filter((e) => e.category === 'curriculum').length})` },
      { id: 'feature', label: `✨ Features (${allEntries.filter((e) => e.category === 'feature').length})` },
      { id: 'praise', label: `⭐ Praise (${allEntries.filter((e) => e.category === 'praise').length})` },
    ];

    this.container.innerHTML = `
      <section class="morris-card" style="margin-top: 28px; padding: 24px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; margin-bottom: 20px;">
          <div>
            <div style="font-size: 0.8rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase;">
              🔍 Atelier Telemetry &amp; Field Diagnostics
            </div>
            <h3 style="font-size: 1.4rem; color: ${tokens.color.morrisIndigo}; margin: 6px 0;">
              Inspector's Logbook &amp; Feedback Deck
            </h3>
            <p style="font-size: 0.95rem; color: #3A4D66; margin: 0; max-width: 650px;">
              Recorded Socratic dialogues, diagnosed cognitive friction points, and telemetry snapshots captured by Inspector Clawey.
            </p>
          </div>

          <div style="display: flex; gap: 10px; align-items: center; flex-wrap: wrap;">
            <button id="btn-export-feedback-json" class="touch-target btn-vellum" style="padding: 8px 14px; font-size: 0.85rem; cursor: pointer;">
              📥 Export JSON
            </button>
            <button id="btn-clear-feedback-log" class="touch-target btn-vellum" style="padding: 8px 14px; font-size: 0.85rem; color: #DC2626; border-color: #DC2626; cursor: pointer;">
              🗑️ Clear All
            </button>
          </div>
        </div>

        <!-- Filter Chips Bar -->
        <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px;">
          ${categories
            .map(
              (cat) => `
            <button
              class="feedback-filter-btn touch-target"
              data-cat="${cat.id}"
              style="
                padding: 6px 14px;
                border-radius: 999px;
                font-size: 0.82rem;
                font-weight: 700;
                cursor: pointer;
                background: ${this.activeCategoryFilter === cat.id ? tokens.color.morrisIndigo : '#FFFDF9'};
                color: ${this.activeCategoryFilter === cat.id ? '#FFFDF9' : tokens.color.morrisIndigo};
                border: 1.5px solid ${tokens.color.gildedBrass};
                transition: all 0.15s ease;
              "
            >
              ${cat.label}
            </button>
          `
            )
            .join('')}
        </div>

        <!-- Entries List or Empty State -->
        ${
          filtered.length === 0
            ? `
          <div style="
            padding: 36px 20px;
            text-align: center;
            background: rgba(250, 246, 237, 0.6);
            border: 2px dashed rgba(197, 160, 89, 0.5);
            border-radius: 14px;
            color: #64748B;
          ">
            <div style="font-size: 2.2rem; margin-bottom: 8px;">🦞</div>
            <div style="font-weight: 700; font-size: 1.05rem; color: ${tokens.color.morrisIndigo};">
              The Inspector's Logbook is Clear
            </div>
            <div style="font-size: 0.9rem; margin-top: 6px; max-width: 480px; margin-left: auto; margin-right: auto;">
              No observations logged for this filter. Tap the floating brass lobster porthole or press <strong>Shift + F</strong> to record an observation from any station.
            </div>
          </div>
        `
            : `
          <div style="display: flex; flex-direction: column; gap: 14px;">
            ${filtered.map((entry) => this.renderEntryCard(entry)).join('')}
          </div>
        `
        }
      </section>
    `;

    this.attachEvents();
    return this.container;
  }

  private renderEntryCard(entry: FeedbackEntry): string {
    const categoryLabels: Record<FeedbackCategory, string> = {
      bug: '🐛 Bug',
      adhd_friction: '🧠 ADHD Ergonomics',
      curriculum: '📚 11+ Curriculum',
      feature: '✨ Feature Idea',
      praise: '⭐ Praise',
    };

    const severityColors: Record<string, string> = {
      blocker: 'background: #FEE2E2; color: #991B1B; border: 1px solid #F87171;',
      high: 'background: #FFEDD5; color: #9A3412; border: 1px solid #FB923C;',
      medium: 'background: #FEF3C7; color: #92400E; border: 1px solid #FCD34D;',
      low: 'background: #E0F2FE; color: #075985; border: 1px solid #7DD3FC;',
    };

    const isResolved = entry.status === 'resolved';

    return `
      <article class="vellum-surface" style="
        padding: 16px 20px;
        border-radius: 12px;
        border: 1.5px solid ${tokens.color.gildedBrass};
        background: #FFFDF9;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      ">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 10px; margin-bottom: 8px;">
          <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span style="font-weight: 800; font-size: 0.8rem; padding: 3px 8px; border-radius: 6px; background: rgba(26, 54, 93, 0.08); color: ${tokens.color.morrisIndigo};">
              ${categoryLabels[entry.category] || entry.category}
            </span>
            <span style="font-weight: 700; font-size: 0.75rem; padding: 2px 8px; border-radius: 6px; text-transform: uppercase; ${severityColors[entry.severity] || ''}">
              ${entry.severity.toUpperCase()}
            </span>
            <span style="font-size: 0.8rem; color: #64748B;">
              📍 <code>${entry.telemetry.route}</code>
            </span>
            <span style="font-size: 0.8rem; color: #64748B;">
              · ${entry.telemetry.activeProfile === 'tester' ? '🧪 Tester Sandbox' : '⭐ Year 4 Tudor'}
            </span>
          </div>

          <div style="font-size: 0.8rem; color: #64748B;">
            ${entry.dateFormatted}
          </div>
        </div>

        <h4 style="margin: 6px 0; font-size: 1.1rem; color: ${tokens.color.morrisIndigo}; font-weight: 800;">
          ${entry.title}
        </h4>
        <p style="margin: 4px 0 12px; font-size: 0.92rem; color: #334155; line-height: 1.5;">
          ${entry.summary}
        </p>

        ${
          entry.suggestedAction
            ? `
          <div style="margin-bottom: 12px; padding: 8px 12px; border-radius: 8px; background: rgba(16, 185, 129, 0.08); border-left: 3px solid #10B981; font-size: 0.85rem; color: #065F46;">
            <strong>Suggested Remediation:</strong> ${entry.suggestedAction}
          </div>
        `
            : ''
        }

        <!-- Dropdown Transcript & Telemetry -->
        <details style="margin-top: 10px; padding: 8px 0; border-top: 1px dashed rgba(197, 160, 89, 0.4); font-size: 0.85rem; color: #475569;">
          <summary style="cursor: pointer; font-weight: 700; color: ${tokens.color.morrisIndigo};">
            Inspect Full Socratic Transcript &amp; Device Telemetry (${entry.transcript.length} turns)
          </summary>
          <div style="margin-top: 10px; padding: 12px; background: rgba(250, 246, 237, 0.5); border-radius: 8px; display: flex; flex-direction: column; gap: 8px;">
            <div style="font-weight: 700; color: ${tokens.color.morrisIndigo};">Transcript:</div>
            ${entry.transcript
              .map(
                (m) => `
              <div>
                <strong>${m.role === 'assistant' ? '🦞 Inspector Clawey' : 'User'}:</strong> ${m.text}
              </div>
            `
              )
              .join('')}
            <div style="font-weight: 700; margin-top: 8px; color: ${tokens.color.morrisIndigo};">Telemetry:</div>
            <div>• Viewport: ${entry.telemetry.viewport.width}×${entry.telemetry.viewport.height} (DPR: ${entry.telemetry.viewport.dpr})</div>
            <div>• Streak: ${entry.telemetry.stats.streakDays}d (${entry.telemetry.stats.isFrozen ? 'Frozen' : 'Active'}) | EP: ${entry.telemetry.stats.totalEp}</div>
            <div>• Practice Time Today: ${entry.telemetry.stats.sessionMinutesToday} min</div>
            <div>• Recent Errors: ${entry.telemetry.recentErrors.length ? entry.telemetry.recentErrors.join(', ') : 'None'}</div>
          </div>
        </details>

        <!-- Actions Row -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 14px; padding-top: 10px; border-top: 1px solid rgba(197, 160, 89, 0.25);">
          <div style="display: flex; gap: 8px;">
            <button class="btn-copy-issue touch-target btn-vellum" data-id="${entry.id}" style="padding: 6px 12px; font-size: 0.8rem; cursor: pointer;">
              📋 Copy GitHub Issue
            </button>
            <button class="btn-toggle-status touch-target btn-vellum" data-id="${entry.id}" style="padding: 6px 12px; font-size: 0.8rem; cursor: pointer;">
              ${isResolved ? '↩️ Mark Open' : '✅ Mark Resolved'}
            </button>
          </div>
          <button class="btn-delete-entry touch-target" data-id="${entry.id}" style="background: none; border: none; color: #94A3B8; font-size: 0.85rem; cursor: pointer; padding: 4px 8px;">
            🗑️ Delete
          </button>
        </div>
      </article>
    `;
  }

  private attachEvents(): void {
    // Filter buttons
    const filterBtns = this.container.querySelectorAll<HTMLButtonElement>('.feedback-filter-btn');
    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const cat = btn.getAttribute('data-cat') as any;
        this.activeCategoryFilter = cat;
        soundEngine.playTap();
        this.render();
      });
    });

    // Copy GitHub Issue buttons
    const copyBtns = this.container.querySelectorAll<HTMLButtonElement>('.btn-copy-issue');
    copyBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        if (!id) return;
        const entry = FeedbackStorage.getEntryById(id);
        if (entry && navigator.clipboard) {
          const md = FeedbackStorage.formatAsGitHubIssue(entry);
          navigator.clipboard.writeText(md).then(() => {
            soundEngine.playSuccess();
            btn.textContent = '✅ Copied!';
            setTimeout(() => {
              btn.textContent = '📋 Copy GitHub Issue';
            }, 2500);
          });
        }
      });
    });

    // Toggle status
    const statusBtns = this.container.querySelectorAll<HTMLButtonElement>('.btn-toggle-status');
    statusBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        if (!id) return;
        const entry = FeedbackStorage.getEntryById(id);
        if (entry) {
          const newStatus = entry.status === 'resolved' ? 'open' : 'resolved';
          FeedbackStorage.updateStatus(id, newStatus);
          soundEngine.playTap();
          this.render();
        }
      });
    });

    // Delete single entry
    const deleteBtns = this.container.querySelectorAll<HTMLButtonElement>('.btn-delete-entry');
    deleteBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        if (!id) return;
        if (confirm('Delete this feedback observation from the Logbook?')) {
          FeedbackStorage.deleteEntry(id);
          soundEngine.playTap();
          this.render();
        }
      });
    });

    // Export JSON
    const exportBtn = this.container.querySelector('#btn-export-feedback-json');
    exportBtn?.addEventListener('click', () => {
      const json = FeedbackStorage.exportAllAsJson();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clawey_feedback_logbook_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      soundEngine.playSuccess();
    });

    // Clear all
    const clearBtn = this.container.querySelector('#btn-clear-feedback-log');
    clearBtn?.addEventListener('click', () => {
      if (confirm('Are you sure you wish to clear all entries in the Inspector Logbook?')) {
        FeedbackStorage.clearAll();
        soundEngine.playTap();
        this.render();
      }
    });
  }
}
